// Step 2 — Audience. Filter rows + add-filter menu + live reach box.
// Role is implicit (students only) per UX doc §7 — users compose additional
// content/engagement/purchase/etc. filters on top.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, SimpleButton, Typo } from '../../../../ui';
import { AUDIENCE_FILTER, createFilter, detectConflicts } from '../../utils/audienceFilterSchema';
import { useAudiencePreview } from '../../hooks/useAudiencePreview';
import { useCampaignsActions } from '../../hooks/useCampaignsActions';
import FilterRow from './FilterRow';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyFilters = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 28px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border: 1px dashed ${({ theme }) => theme.colors.NEU_300};
  border-radius: 10px;
  text-align: center;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  position: relative;
`;

const MenuPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.BOX_SHADOW_SMALL};
  min-width: 220px;
  z-index: 10;
  overflow: hidden;
`;

const MenuItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme, $disabled }) => ($disabled ? theme.colors.NEU_300 : theme.colors.NEU_PRIMARY)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover { background: ${({ theme }) => theme.colors.NEU_50}; }
`;

const ReachBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => theme.colors.PRIMARY_LIGHT};
  border: 1px solid ${({ theme }) => theme.colors.PRIMARY_LIGHT};
  border-radius: 12px;
  padding: 22px 24px;
  flex-wrap: wrap;
`;

const ReachStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReachCount = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.PRIMARY};
`;

const ReachLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const ReachActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ $variant, theme }) => ($variant === 'warn' ? theme.colors.MARKER_LIGHT : theme.colors.ERROR_LIGHT)};
  color: ${({ $variant, theme }) => ($variant === 'warn' ? theme.colors.MARKER : theme.colors.ERROR_RED)};
  border-radius: 10px;
  font-size: 13px;
`;

const FILTER_MENU = [
  { id: AUDIENCE_FILTER.ENROLLED_IN, label: 'Enrolled in course' },
  { id: AUDIENCE_FILTER.NOT_ENROLLED, label: 'Not enrolled in course' },
  { id: AUDIENCE_FILTER.COMPLETED_COURSE, label: 'Completed course' },
  { id: AUDIENCE_FILTER.COMPLETION_RATE, label: 'Course completion rate' },
  { id: AUDIENCE_FILTER.GROUP, label: 'In group' },
  { id: AUDIENCE_FILTER.HAS_PURCHASED, label: 'Has purchased' },
  { id: AUDIENCE_FILTER.PURCHASE_AMOUNT, label: 'Purchase amount' },
  { id: AUDIENCE_FILTER.LAST_ACTIVE_BEFORE, label: 'Last active before' },
  { id: AUDIENCE_FILTER.LAST_ACTIVE_AFTER, label: 'Last active after' },
  { id: AUDIENCE_FILTER.EMAIL_DOMAIN, label: 'Email domain' },
];

const AddFilterMenu = ({ activeTypes, onPick }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close menu on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <SimpleButton variant='tertiary' startIcon='Add' onClick={() => setOpen((v) => !v)}>
        {t('campaigns.audience.addFilter', 'Add filter')}
      </SimpleButton>
      {open && (
        <MenuPanel role='menu'>
          {FILTER_MENU.map((opt) => {
            const isActive = activeTypes.has(opt.id);
            return (
              <MenuItem
                key={opt.id}
                role='menuitem'
                tabIndex={0}
                $disabled={isActive}
                onClick={() => { if (!isActive) { onPick(opt.id); setOpen(false); } }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isActive) { onPick(opt.id); setOpen(false); }
                }}
              >
                {t(`campaigns.filter.${opt.id}`, opt.label)}{isActive ? ' · ' + t('campaigns.audience.alreadyAdded', 'already added') : ''}
              </MenuItem>
            );
          })}
        </MenuPanel>
      )}
    </div>
  );
};

const StepAudience = ({ draft, onChange }) => {
  const { t } = useTranslation();
  const filters = draft?.audience?.filters ?? [];
  const excludeUserIds = draft?.audience?.excludeUserIds ?? [];
  const preview = useAudiencePreview({ filters, excludeUserIds });
  const { exportAudience } = useCampaignsActions();
  const conflicts = detectConflicts(filters);

  // Mirror the server's count into the draft so the summary aside +
  // step-validation see it. Guard on change to avoid an update loop.
  useEffect(() => {
    if (preview.count != null && preview.count !== draft?.audience?.estimatedReach) {
      onChange({
        ...draft,
        audience: { ...draft.audience, estimatedReach: preview.count },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.count]);

  const updateFilter = useCallback((next) => {
    onChange({
      ...draft,
      audience: {
        ...draft.audience,
        filters: filters.map((f) => (f.id === next.id ? next : f)),
      },
    });
  }, [draft, filters, onChange]);

  const removeFilter = useCallback((id) => {
    onChange({
      ...draft,
      audience: { ...draft.audience, filters: filters.filter((f) => f.id !== id) },
    });
  }, [draft, filters, onChange]);

  const addFilter = useCallback((type) => {
    const next = createFilter(type);
    onChange({
      ...draft,
      audience: { ...draft.audience, filters: [...filters, next] },
    });
  }, [draft, filters, onChange]);

  const handleExport = useCallback(() => {
    exportAudience({ filters, format: 'csv' }).catch(() => {});
  }, [exportAudience, filters]);

  const activeTypes = new Set(filters.map((f) => f.type));

  return (
    <Card>
      <Header>
        <Typo.ContentSmallHeader>{t('campaigns.audience.title', 'Who should receive this?')}</Typo.ContentSmallHeader>
        <Typo.SmallText color='textSecondary'>
          {t(
            'campaigns.audience.description',
            'Campaigns are sent to students only — teachers and admins are automatically excluded. Add filters below to narrow your audience further.',
          )}
        </Typo.SmallText>
      </Header>

      {filters.length === 0 ? (
        <EmptyFilters>
          <Icon.Info size='small' />
          <Typo.MediumText>{t('campaigns.audience.noFilters', 'No filters yet')}</Typo.MediumText>
          <Typo.SmallText color='textSecondary'>
            {t('campaigns.audience.noFiltersHint', 'Without filters, the campaign will be sent to every student in your store.')}
          </Typo.SmallText>
        </EmptyFilters>
      ) : (
        filters.map((f) => (
          <FilterRow
            key={f.id}
            filter={f}
            onChange={updateFilter}
            onRemove={() => removeFilter(f.id)}
          />
        ))
      )}

      <Toolbar>
        <AddFilterMenu activeTypes={activeTypes} onPick={addFilter} />
        <SimpleButton variant='tertiary' startIcon='User' onClick={() => {}} disabled>
          {t('campaigns.audience.excludeUsers', 'Exclude users')}
        </SimpleButton>
      </Toolbar>

      {conflicts.length > 0 && (
        <Banner>
          <Icon.Info size='small' />
          {t(
            'campaigns.audience.conflict',
            'Mutually exclusive filters — a course appears in both an "enrolled" and "not enrolled" filter.',
          )}
        </Banner>
      )}

      <ReachBox>
        <ReachStats>
          <ReachCount>
            {preview.isLoading || preview.count == null
              ? '—'
              : filters.length === 0
                ? t('campaigns.audience.allStudents', 'All students')
                : preview.count.toLocaleString()}
          </ReachCount>
          <ReachLabel>
            {filters.length === 0
              ? t('campaigns.audience.noFiltersReachLabel', 'sent to every student in the store')
              : t('campaigns.audience.estimatedRecipients', 'estimated recipients')}
            {preview.isStale ? ` · ${t('campaigns.audience.refreshing', 'refreshing…')}` : ''}
          </ReachLabel>
        </ReachStats>
        <ReachActions>
          <SimpleButton variant='tertiary' startIcon='Download' onClick={handleExport} disabled={preview.count === 0}>
            {t('campaigns.audience.exportCsv', 'Export CSV')}
          </SimpleButton>
        </ReachActions>
      </ReachBox>

      {preview.count === 0 && filters.length > 0 && (
        <Banner $variant='warn'>
          <Icon.Info size='small' />
          {t('campaigns.audience.empty', 'No students match these filters. Try broadening the criteria.')}
        </Banner>
      )}

      {preview.suppressedForConsent > 0 && (
        <Banner $variant='warn'>
          <Icon.Info size='small' />
          {t(
            'campaigns.audience.suppressed',
            '{{n}} students have unsubscribed from marketing emails and will not be included in the send.',
            { n: preview.suppressedForConsent.toLocaleString() },
          )}
        </Banner>
      )}
    </Card>
  );
};

export default StepAudience;
